import type { ReactNode } from 'react'
import { LuArchive, LuArchiveRestore, LuArrowDown, LuArrowUp, LuInfo, LuX } from 'react-icons/lu'
import { Hint } from '../../../components/ui/Hint.tsx'
import { IconButton } from '../../../components/ui/IconButton.tsx'
import { cn } from '../../../lib/cn.ts'
import { hint } from '../../../lib/hint.ts'

export interface GridHeader {
  label: string
  hint?: string
  align?: 'start' | 'center' | 'end'
}

const ALIGN = { start: 'justify-start', center: 'justify-center', end: 'justify-end' } as const

/**
 * Editable settings table. `columns` is a static Tailwind `grid-cols-[…]` class shared by the
 * header and every row; `minWidth` keeps it readable on phones (it scrolls sideways instead).
 */
export function SettingsGrid({
  label,
  columns,
  minWidth,
  headers,
  children,
}: {
  label: string
  columns: string
  minWidth: string
  headers: GridHeader[]
  children: ReactNode
}) {
  return (
    <div className="overflow-x-auto overscroll-x-contain border-y border-border">
      <table aria-label={label} className={cn('block border-collapse', minWidth)}>
        <thead className="block">
          <tr
            className={cn(
              'grid items-center gap-2 bg-surface-2 px-4 py-2 text-[12.5px] font-medium text-muted',
              columns,
            )}
          >
            {headers.map((h, i) => (
              <th
                key={h.label || i}
                scope="col"
                className={cn(
                  'flex min-w-0 items-center gap-1 font-medium',
                  ALIGN[h.align ?? 'start'],
                )}
              >
                {h.label && <span className="truncate">{h.label}</span>}
                {h.hint && (
                  <Hint text={h.hint} focusable>
                    <LuInfo aria-hidden className="size-3.5 text-faint" />
                  </Hint>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="block">{children}</tbody>
      </table>
    </div>
  )
}

export function GridRow({
  columns,
  archived,
  cells,
  actions,
}: {
  columns: string
  archived?: boolean
  cells: ReactNode[]
  actions: ReactNode
}) {
  return (
    <tr
      aria-disabled={archived || undefined}
      className={cn(
        'grid items-center gap-2 border-t border-border px-4 py-2 first:border-t-0',
        columns,
      )}
    >
      {cells.map((cell, i) => (
        <td
          // biome-ignore lint/suspicious/noArrayIndexKey: cells are positional columns
          key={i}
          className={cn('min-w-0', archived && 'opacity-50')}
        >
          {cell}
        </td>
      ))}
      <td data-tour="settings-row-actions" className="flex items-center justify-end gap-0.5">
        {actions}
      </td>
    </tr>
  )
}

/** A disabled control can't fire hover events, so its reason lives on a wrapper. */
export function WithReason({ reason, children }: { reason?: string | null; children: ReactNode }) {
  if (!reason) return children
  return <Hint text={reason}>{children}</Hint>
}

export interface RowActionsProps {
  name: string
  index?: number
  count?: number
  archived: boolean
  /** Not saved yet: offer Remove instead of Archive. */
  isNew?: boolean
  onMove?: (dir: -1 | 1) => void
  onArchive: (archived: boolean) => void
  onRemove?: () => void
  /** Why archiving is not allowed for this row (disables the button). */
  archiveBlocked?: string | null
}

export const ARCHIVE_HINT =
  'Archive: hide from new tickets and pickers. Nothing is deleted so ticket history never breaks.'

export function RowActions({
  name,
  index = 0,
  count = 1,
  archived,
  isNew,
  onMove,
  onArchive,
  onRemove,
  archiveBlocked,
}: RowActionsProps) {
  const label = name.trim() || 'item'
  return (
    <>
      {onMove && (
        <>
          <IconButton
            size="sm"
            aria-label={`Move ${label} up`}
            disabled={index === 0}
            onClick={() => onMove(-1)}
            {...hint('Move up')}
          >
            <LuArrowUp />
          </IconButton>
          <IconButton
            size="sm"
            aria-label={`Move ${label} down`}
            disabled={index === count - 1}
            onClick={() => onMove(1)}
            {...hint('Move down')}
          >
            <LuArrowDown />
          </IconButton>
        </>
      )}
      {isNew && onRemove ? (
        <IconButton
          size="sm"
          aria-label={`Remove ${label}`}
          onClick={onRemove}
          {...hint('Remove: it hasn’t been saved yet')}
        >
          <LuX />
        </IconButton>
      ) : archived ? (
        <WithReason reason={archiveBlocked}>
          <IconButton
            size="sm"
            aria-label={`Restore ${label}`}
            disabled={!!archiveBlocked}
            onClick={() => onArchive(false)}
            {...(archiveBlocked ? {} : hint('Restore: make it available again'))}
            className="text-teal-fg hover:text-teal-fg"
          >
            <LuArchiveRestore />
          </IconButton>
        </WithReason>
      ) : (
        <WithReason reason={archiveBlocked}>
          <IconButton
            size="sm"
            aria-label={`Archive ${label}`}
            disabled={!!archiveBlocked}
            onClick={() => onArchive(true)}
            {...(archiveBlocked ? {} : hint(ARCHIVE_HINT))}
          >
            <LuArchive />
          </IconButton>
        </WithReason>
      )}
    </>
  )
}
